<template>
    <TemplateModalEx v-model:show="computedShow" class="passwordManagerDialog">
        <template #title>追加</template>
        <template #body>
            <span style="margin-bottom: 16px; height:20px;">利用を許可するパスワードマネージャーを選択してください。</span>
            <ListTitleBar ref="refTitleBar" titleCaption="アプリケーション一覧" unit="個" :rowCount="rowCount" :rowCountArea="true"
                :isUseMenuItemList="true" :selectCount="selectCount" :isShowOperationButton="false"
                :enableSearchText="true" @onSearchBoxKeydown="search" @onBtnClear="search" />
            <ListCtrl ref="refPasswordManagedListCtrl" :columns="PASSWORD_MANAGER_COLUMN" :selectType="3"
                :getCellTextFunc="getCellText" :getCellHTMLFunc="getCellHTML" @onClickCell="onClickCell"
                @onSelectIndexChanged="onSelectedIndicesChanged" :sortDeleteIndex="false" style="height: 300px" />
        </template>
        <template #footer>
            <div class="modal-footer modal-footer-scv">
                <div class="d-flex justify-content-center">
                    <div class="center-content">
                        <button class="btn-scv-blue margin-r" @click="onApply"
                            :disabled="selectedPackageNames.size === 0">OK</button>
                        <button class="btn-scv-white" @click="onClose">キャンセル</button>
                    </div>
                </div>
            </div>
        </template>
    </TemplateModalEx>
    <Teleport to="body">
        <!-- Google Playストアのアプリケーションの詳細情報 -->
        <AppMgrApplicationDetailModal ref="refDetailModal" v-model:show="showDetailDialog"/>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { cloneDeep } from 'lodash';
import ListTitleBar from '@/components/ListTitleBar.vue';
import { ListCtrl } from 'lib-vue-v2/components';
import { SearchColumnData, SearchColumnType } from '@/models/SearchColumnData';
import { TemplateModalEx } from "lib-vue-v2/components";
import { AndroidAppMgrApplicationSetting } from '@/models/Mobile/AndroidAppDist/AndroidAppMgrApplicationSetting'
import { AndroidAppDistDefinition } from "@/components/Mobile/AndroidAppDist/AndroidAppDistDefinition";
import { ApplicationPolicyConverter } from '@/models/Mobile/AndroidAppDist/ApplicationPolicyConverter'
import AppMgrApplicationDetailModal from '@/components/Mobile/AndroidAppDist/AppMgrApplicationDetailModal.vue';

// --- Define ---
const PASSWORD_MANAGER_COLUMN: Array<SearchColumnData> = [
    { columnID: 0, dataType: SearchColumnType.icon | SearchColumnType.no_sort, text: "" },
    { columnID: 1, dataType: SearchColumnType.string | SearchColumnType.link, text: "アプリケーション名" },
    { columnID: 2, dataType: SearchColumnType.string, text: "パッケージ名" },
    { columnID: 3, dataType: SearchColumnType.string, text: "ステータス" },
];

interface Props {
    show: boolean;
    androidAppMgrApplicationSetting: AndroidAppMgrApplicationSetting[];
}

const props = withDefaults(defineProps<Props>(), {});

interface Emits {
    (event: "update:show", newValue: boolean): void;
    (event: "modified", packageNames: string[]): void;
}

const emit = defineEmits<Emits>();

// 表示非表示の切り替え
const refShow = ref(false);
const computedShow = computed({
    get: () => refShow.value,
    set: (newValue: boolean) => emit("update:show", newValue)
});

// --- ref ---
const refTitleBar = ref<InstanceType<typeof ListTitleBar> | null>(null);
const refPasswordManagedListCtrl = ref<InstanceType<typeof ListCtrl> | null>(null);
const refDetailModal = ref<InstanceType<typeof AppMgrApplicationDetailModal> | null>(null);

// 編集中データ
const editSetting = ref<AndroidAppMgrApplicationSetting[]>([]);

// アプリ詳細ダイアログの表示／非表示
const showDetailDialog = ref(false);

// パッケージ名で管理する選択状態のSet
const selectedPackageNames = ref<Set<string>>(new Set());

// ポリシー⇔UIデータ
const converter = new ApplicationPolicyConverter();

// --- Watch ---
watch(() => props.show, async () => {
    if (props.show) {
        initData();
        refShow.value = true;
    } else {
        refShow.value = false;
    }
}, { immediate: true });

const rowCount = computed(() => {
    if (refPasswordManagedListCtrl.value === null) return 0;
    return refPasswordManagedListCtrl.value.getDisplayListIndex().length;
});

const selectCount = computed(() => {
    return selectedPackageNames.value.size;
});

// 適用 (OKボタン)
function onApply(): void {
    // リストUIの状態に依存せず、メモリ内のSetから直接配列に変換してEmitします
    const selectedList = Array.from(selectedPackageNames.value);
    emit("modified", selectedList);
    emit("update:show", false);
}

// 閉じる
function onClose(): void {
    emit("update:show", false);
}

// 初期化
const initData = async () => {
    refTitleBar.value?.SearchStart("");
    editSetting.value = cloneDeep(props.androidAppMgrApplicationSetting);
    selectedPackageNames.value = new Set<string>();
    updateList();
}

// 検索
const search = (searchText: string = ""): void => {
    if (refPasswordManagedListCtrl.value) {
        refPasswordManagedListCtrl.value.search(searchText);
        updateCheckStatus();
    }
}

// リスト更新
const updateList = () => {
    if (refPasswordManagedListCtrl.value) {
        refPasswordManagedListCtrl.value.updateList(editSetting.value.length);
        updateCheckStatus();
    }
}

// チェックボックスの状態を「現在表示中のリスト」に反映させる処理
const updateCheckStatus = () => {
    const selectIndex: number[] = [];
    editSetting.value.forEach((item, index) => {
        const pkgName = converter.GetPackageName(item.policyJson);
        if (selectedPackageNames.value.has(pkgName)) {
            selectIndex.push(index);
        }
    });

    if (refPasswordManagedListCtrl.value) {
        refPasswordManagedListCtrl.value.selectRows(selectIndex);
    }
};

// リストUI側で選択チェックが変更された際の処理
const onSelectedIndicesChanged = (selectedIndices: number[]) => {
    if (!refPasswordManagedListCtrl.value) return;
    const newSelectedSet = new Set(selectedPackageNames.value);
    const currentlyCheckedInView = new Set<string>();
    selectedIndices.forEach(idx => {
        const item = editSetting.value[idx];
        if (item) {
            const pkgName = converter.GetPackageName(item.policyJson);
            currentlyCheckedInView.add(pkgName);
        }
    });

    const displayIndices: number[] = refPasswordManagedListCtrl.value.getDisplayListIndex();
    displayIndices.forEach(idx => {
        const item = editSetting.value[idx];
        if (!item) return;
        const pkgName = converter.GetPackageName(item.policyJson);
        if (currentlyCheckedInView.has(pkgName)) {
            newSelectedSet.add(pkgName);
        } else {
            newSelectedSet.delete(pkgName);
        }
    });
    selectedPackageNames.value = newSelectedSet;
};


// リスト作成処理(テキスト)
const getCellText = (columnId: number, index: number): string => {
    switch (columnId) {
        case 1:
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE === editSetting.value[index].basicInfo.appSettingType) {
                return editSetting.value[index].basicInfo.appName;
            }
            return "";
        case 2:
            return converter.GetPackageName(editSetting.value[index].policyJson);
        case 3:
            return converter.GetDisabledText(editSetting.value[index].policyJson);
    }
    return "";
}

// リスト作成処理(HTML)
const getCellHTML = (columnId: number, index: number): string => {
    switch (columnId) {
        case 0:
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE === editSetting.value[index].basicInfo.appSettingType) {
                return "<div style=\"padding: 8px 0px;\"><div style=\"height: 32px; width: 32px;\"></div></div>";
            }
            if (0 !== editSetting.value[index].basicInfo.iconUrl.length) {
                return "<div style=\"padding: 8px 0px;\"><img src=\"" + editSetting.value[index].basicInfo.iconUrl + "\" style=\"height: 32px; width: 32px; border-radius: 7px; border: 0.5px solid #E6EBF0\"></div>";
            }
            else {
                return "<div style=\"padding: 8px 0px;\"><div style=\"height: 32px; width: 32px; border-radius: 7px; border: 0.5px solid #E6EBF0\"></div></div>";
            }
        case 1:
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE === editSetting.value[index].basicInfo.appSettingType) {
                return "";
            }
            return "<span class=\"scv-link-text\" style=\"cursor: pointer;\">" + editSetting.value[index].basicInfo.appName + "</span>";
    }
    return "";
}

// セルクリック時
const onClickCell = (columnId: number, index: number): void => {
    switch (columnId) {
        case 1:
            if (AndroidAppDistDefinition.APP_SETTING_TYPE_FREE == editSetting.value[index].basicInfo.appSettingType) return;
            showDetail(editSetting.value[index]); // props ではなく editSetting を参照するよう修正
            return;
    }
}

// 詳細表示
const showDetail = (data: AndroidAppMgrApplicationSetting): void => {
    if (!refDetailModal.value) return;
    refDetailModal.value.SetData(data.basicInfo);
    showDetailDialog.value = true;
}
</script>

<style lang="scss" scoped>
.passwordManagerDialog {
    :deep(.modal-content) {
        width: 960px;
    }
}

.margin-r {
    margin-right: 24px;
}
</style>
